import { useState, useEffect, useCallback, useMemo } from "react";
import { useTelegram } from "./useTelegram";
import { IS_DEMO, getDemoConfig, applyDemoContent } from "@/lib/demo";
import { useDemoCustomization } from "@/lib/customizer";

export interface SocialLink {
  id: string;
  platform: string;
  url: string;
  enabled: boolean;
  icon?: string;
}

export interface AppConfig {
  profile: {
    name: string;
    bio: string;
    telegramUsername: string;
    phone: string;
    avatarUrl?: string;
    bannerUrl?: string;
  };
  photos?: string[];
  socialLinks: SocialLink[];
  pricing: {
    vipMonthly: number;
    vipYearly: number;
    currency: string;
    stars_price_new?: number;
    stars_price_recurrent?: number;
    stars_enabled?: boolean;
  };
  channel: {
    inviteLink: string;
    freeChannelInviteLink: string;
    price: number;
  };
  sales: {
    payment_method: string;
    payment_target: string;
  };
  contact: {
    telegram: string;
    phone: string;
  };
  bot: {
    botUsername: string;
    agentName: string;
  };
  wishlist: {
    url: string;
    enabled: boolean;
    provider: "throne" | "wishtender" | "amazon" | "buymeacoffee" | "kofi" | "other";
  };
}

const env = import.meta.env;

const DEFAULT_CONFIG: AppConfig = {
  profile: {
    name: env.VITE_PROFILE_NAME || "",
    bio: env.VITE_PROFILE_BIO || "",
    telegramUsername: env.VITE_PROFILE_TELEGRAM || "",
    phone: env.VITE_PROFILE_PHONE || "",
  },
  socialLinks: [
    { id: "x", platform: "X", url: env.VITE_SOCIAL_X_URL || "", enabled: !!env.VITE_SOCIAL_X_URL },
    { id: "instagram", platform: "Instagram", url: env.VITE_SOCIAL_INSTAGRAM_URL || "", enabled: !!env.VITE_SOCIAL_INSTAGRAM_URL },
    { id: "tiktok", platform: "TikTok", url: env.VITE_SOCIAL_TIKTOK_URL || "", enabled: !!env.VITE_SOCIAL_TIKTOK_URL },
    { id: "onlyfans", platform: "OnlyFans", url: env.VITE_SOCIAL_ONLYFANS_URL || "", enabled: !!env.VITE_SOCIAL_ONLYFANS_URL },
    { id: "tumblr", platform: "Tumblr", url: env.VITE_SOCIAL_TUMBLR_URL || "", enabled: !!env.VITE_SOCIAL_TUMBLR_URL },
    { id: "xxxfollow", platform: "XXXFollow", url: env.VITE_SOCIAL_XXXFOLLOW_URL || "", enabled: !!env.VITE_SOCIAL_XXXFOLLOW_URL },
  ],
  pricing: {
    vipMonthly: Number(env.VITE_VIP_MONTHLY || 30000),
    vipYearly: Number(env.VITE_VIP_YEARLY || 50000),
    currency: env.VITE_PRICE_CURRENCY || "COP",
    stars_price_new: Number(env.VITE_STARS_PRICE_NEW || 450),
    stars_price_recurrent: Number(env.VITE_STARS_PRICE_RECURRENT || 750),
    stars_enabled: env.VITE_STARS_ENABLED !== "false",
  },
  channel: {
    inviteLink: env.VITE_CHANNEL_INVITE_LINK || "",
    freeChannelInviteLink: env.VITE_FREE_CHANNEL_INVITE_LINK || "",
    price: Number(env.VITE_CHANNEL_PRICE || 0),
  },
  sales: {
    payment_method: env.VITE_PAYMENT_METHOD || "",
    payment_target: env.VITE_PAYMENT_TARGET || "",
  },
  contact: {
    telegram: env.VITE_CONTACT_TELEGRAM || "",
    phone: env.VITE_CONTACT_PHONE || "",
  },
  bot: {
    botUsername: env.VITE_BOT_USERNAME || "",
    agentName: env.VITE_PROFILE_NAME || "",
  },
  wishlist: {
    url: env.VITE_WISHLIST_URL || "",
    enabled: env.VITE_WISHLIST_ENABLED === "true",
    provider: (env.VITE_WISHLIST_PROVIDER || "amazon") as any,
  }
};

const STORAGE_KEY = "app_config_v2";

export const useAppConfig = () => {
  const { isInTelegram } = useTelegram();
  const [config, setConfig] = useState<AppConfig>(() => (IS_DEMO ? getDemoConfig(DEFAULT_CONFIG) : DEFAULT_CONFIG));
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  // Demo build: whatever the prospect typed in the customizer shows up in every section at once
  const demoContent = useDemoCustomization().content;
  const demoConfig = useMemo(() => (IS_DEMO ? applyDemoContent(getDemoConfig(DEFAULT_CONFIG), demoContent) : null), [demoContent]);

  // Cargar configuración
  useEffect(() => {
    const loadConfig = async () => {
      // Demo build: fictional profile only — never read the production API or saved configs
      if (IS_DEMO) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);

      // 1. Intentar cargar desde el servidor dinámico (/api/app-config)
      try {
        const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
        const res = await fetch(`${apiUrl}/api/app-config`);
        if (res.ok) {
          const json = await res.json();
          if (json.ok && json.data) {
            const d = json.data;
            setConfig(prev => ({
              ...prev,
              profile: {
                ...prev.profile,
                name: d.profile?.name || prev.profile.name,
                bio: d.profile?.bio || prev.profile.bio,
                telegramUsername: d.profile?.telegram || prev.profile.telegramUsername,
                phone: d.profile?.phone || prev.profile.phone,
                avatarUrl: d.profile?.avatarUrl,
                bannerUrl: d.profile?.bannerUrl
              },
              photos: Array.isArray(d.photos) && d.photos.length > 0 ? d.photos : prev.photos,
              pricing: {
                ...prev.pricing,
                vipMonthly: Number(d.pricing?.price_new ?? prev.pricing.vipMonthly),
                vipYearly: Number(d.pricing?.price_recurrent ?? prev.pricing.vipYearly),
                currency: d.pricing?.currency || prev.pricing.currency,
                stars_price_new: Number(d.pricing?.stars_price_new ?? prev.pricing.stars_price_new),
                stars_price_recurrent: Number(d.pricing?.stars_price_recurrent ?? prev.pricing.stars_price_recurrent),
                stars_enabled: d.pricing?.stars_enabled !== undefined
                  ? Boolean(d.pricing.stars_enabled)
                  : (prev.pricing.stars_enabled !== false)
              },
              channel: {
                ...prev.channel,
                inviteLink: d.channels?.channel_invite || prev.channel.inviteLink,
                freeChannelInviteLink: d.channels?.free_channel_invite || prev.channel.freeChannelInviteLink
              },
              sales: {
                ...prev.sales,
                payment_method: d.payment?.payment_method || prev.sales.payment_method,
                payment_target: d.payment?.payment_target_masked || prev.sales.payment_target
              },
              socialLinks: Array.isArray(d.socialLinks) && d.socialLinks.length > 0 ? d.socialLinks : prev.socialLinks
            }));
            setIsLoading(false);
            return;
          }
        }
      } catch (err) {
        // Fallback a almacenamiento local o CloudStorage
      }

      const fallbackToLocalStorage = () => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setConfig({
              ...DEFAULT_CONFIG,
              ...parsed,
              profile: { ...DEFAULT_CONFIG.profile, ...parsed.profile },
              pricing: { ...DEFAULT_CONFIG.pricing, ...parsed.pricing },
              channel: { ...DEFAULT_CONFIG.channel, ...parsed.channel },
              sales: { ...DEFAULT_CONFIG.sales, ...parsed.sales },
              contact: { ...DEFAULT_CONFIG.contact, ...parsed.contact },
              bot: { ...DEFAULT_CONFIG.bot, ...parsed.bot },
              wishlist: { ...DEFAULT_CONFIG.wishlist, ...parsed.wishlist },
              socialLinks: Array.isArray(parsed.socialLinks) ? parsed.socialLinks : DEFAULT_CONFIG.socialLinks,
            });
          } catch (e) {
            console.error("Error parsing localStorage config:", e);
          }
        }
        setIsLoading(false);
      };

      try {
        const hasCloudStorage = isInTelegram && 
          window.Telegram?.WebApp?.isVersionAtLeast?.('6.9') && 
          window.Telegram?.WebApp?.CloudStorage;

        if (hasCloudStorage) {
          window.Telegram.WebApp.CloudStorage.getItem(STORAGE_KEY, (error, value) => {
            if (!error && value) {
              try {
                const parsed = JSON.parse(value);
                setConfig({
                  ...DEFAULT_CONFIG,
                  ...parsed,
                  profile: { ...DEFAULT_CONFIG.profile, ...parsed.profile },
                  pricing: { ...DEFAULT_CONFIG.pricing, ...parsed.pricing },
                  channel: { ...DEFAULT_CONFIG.channel, ...parsed.channel },
                  sales: { ...DEFAULT_CONFIG.sales, ...parsed.sales },
                  contact: { ...DEFAULT_CONFIG.contact, ...parsed.contact },
                  bot: { ...DEFAULT_CONFIG.bot, ...parsed.bot },
                  wishlist: { ...DEFAULT_CONFIG.wishlist, ...parsed.wishlist },
                  socialLinks: Array.isArray(parsed.socialLinks) ? parsed.socialLinks : DEFAULT_CONFIG.socialLinks,
                });
                setIsLoading(false);
              } catch (e) {
                console.error("Error parsing CloudStorage config, falling back:", e);
                fallbackToLocalStorage();
              }
            } else {
              fallbackToLocalStorage();
            }
          });
        } else {
          fallbackToLocalStorage();
        }
      } catch (error) {
        console.error("Error loading config via CloudStorage, falling back:", error);
        fallbackToLocalStorage();
      }
    };

    loadConfig();
  }, [isInTelegram]);

  // Guardar configuración
  const saveConfig = useCallback(async (newConfig: AppConfig) => {
    if (IS_DEMO) return; // demo never persists or reaches the API
    setConfig(newConfig);
    try {
      const configString = JSON.stringify(newConfig);
      
      const hasCloudStorage = isInTelegram && 
        window.Telegram?.WebApp?.isVersionAtLeast?.('6.9') && 
        window.Telegram?.WebApp?.CloudStorage;

      if (hasCloudStorage) {
        try {
          window.Telegram.WebApp.CloudStorage.setItem(STORAGE_KEY, configString, (error) => {
            if (error) {
              console.error("Error saving to CloudStorage:", error);
            }
          });
        } catch (csError) {
          console.error("Error calling CloudStorage.setItem:", csError);
        }
      }
      localStorage.setItem(STORAGE_KEY, configString);

      // Si estamos en Telegram con initData, sincronizar con el backend
      const rawInitData = window.Telegram?.WebApp?.initData;
      if (rawInitData) {
        try {
          const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
          fetch(`${apiUrl}/api/admin/settings`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-Telegram-Init-Data': rawInitData
            },
            body: JSON.stringify({
              pricing: {
                price_new: newConfig.pricing.vipMonthly,
                price_recurrent: newConfig.pricing.vipYearly,
                currency: newConfig.pricing.currency,
                stars_price_new: newConfig.pricing.stars_price_new,
                stars_price_recurrent: newConfig.pricing.stars_price_recurrent,
                stars_enabled: newConfig.pricing.stars_enabled
              }
            })
          }).catch((err) => {
            console.warn("Error background syncing settings to backend:", err);
          });
        } catch (apiErr) {
          console.warn("Could not initiate backend sync:", apiErr);
        }
      }

      return true;
    } catch (error) {
      console.error("Error saving config:", error);
      return false;
    }
  }, [isInTelegram]);

  // Actualizar un campo específico
  const updateField = useCallback(<K extends keyof AppConfig>(
    section: K,
    value: AppConfig[K]
  ) => {
    const newConfig = { ...config, [section]: value };
    saveConfig(newConfig);
  }, [config, saveConfig]);

  // Agregar/editar/eliminar red social
  const updateSocialLink = useCallback((link: SocialLink) => {
    const links = config.socialLinks.filter(l => l.id !== link.id);
    links.push(link);
    updateField("socialLinks", links);
  }, [config.socialLinks, updateField]);

  const removeSocialLink = useCallback((id: string) => {
    const links = config.socialLinks.filter(l => l.id !== id);
    updateField("socialLinks", links);
  }, [config.socialLinks, updateField]);

  // Verificación de admin
  const checkAdmin = useCallback(async () => {
    // Demo build: the in-app panel shows a fictional creator's data, anyone may look
    if (IS_DEMO) {
      setIsAdmin(true);
      return true;
    }

    const envIds = (import.meta.env.VITE_ADMIN_IDS || "")
      .split(",")
      .map((id: string) => Number(id.trim()))
      .filter((n: number) => Number.isFinite(n));

    // Permitir acceso admin solo en desarrollo local para facilitar pruebas
    if (import.meta.env.DEV) {
      setIsAdmin(true);
      return true;
    }

    if (isInTelegram) {
      const tg = window.Telegram?.WebApp;
      const userId = tg?.initDataUnsafe?.user?.id;
      if (userId && envIds.includes(userId)) {
        setIsAdmin(true);
        return true;
      }
    }

    setIsAdmin(false);
    return false;
  }, [isInTelegram]);

  // Resetear a defaults
  const resetConfig = useCallback(() => {
    saveConfig(DEFAULT_CONFIG);
  }, [saveConfig]);

  // Exportar config
  const exportConfig = useCallback(() => {
    return JSON.stringify(config, null, 2);
  }, [config]);

  // Importar config
  const importConfig = useCallback((json: string) => {
    try {
      const parsed = JSON.parse(json);
      saveConfig({ ...DEFAULT_CONFIG, ...parsed });
      return true;
    } catch (e) {
      return false;
    }
  }, [saveConfig]);

  return {
    config: demoConfig ?? config,
    isLoading,
    isAdmin,
    saveConfig,
    updateField,
    updateSocialLink,
    removeSocialLink,
    checkAdmin,
    resetConfig,
    exportConfig,
    importConfig,
  };
};

export default useAppConfig;
